import { useId } from 'react';
import type { FC } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next/pages';

import { Avatar, Box, Divider, Menu, Text, UnstyledButton } from '@highlight-cp/core-ui';

import ImpersonatingBadge from '../impersonating-badge';
import { TEST_ID } from '../../constants/testIds';
import classes from './top-nav-avatar-menu.module.css';

interface TopNavPersonName {
  firstName?: string | null;
  lastName?: string | null;
}

export interface TopNavViewer {
  /** The person the viewer is acting as — the customer, on either employee path. */
  user: TopNavPersonName | null;
  /** A Highlight employee is behind the screen rather than the customer. */
  isImpersonating?: boolean;
  impersonator?: TopNavPersonName | null;
}

export interface TopNavAvatarMenuProps {
  viewer: TopNavViewer;
  onLogout?: () => void;
  menuItems?: { label: string; href: string }[];
}

const TopNavAvatarMenu: FC<TopNavAvatarMenuProps> = ({ viewer, onLogout, menuItems }) => {
  const { isImpersonating, impersonator, user } = viewer;
  const { t } = useTranslation();
  const impersonatingBadgeId = useId();
  const userName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  return (
    <Menu
      position="bottom"
      offset={5}
      withArrow
      transitionProps={{ duration: 150, transition: 'scale-y', timingFunction: 'ease-out' }}
    >
      <Menu.Target>
        <UnstyledButton
          aria-label={t('User menu')}
          // The badge is the only warning that an employee is behind the screen, and
          // the button's own name never changes — describe it explicitly.
          aria-describedby={isImpersonating ? impersonatingBadgeId : undefined}
          className={classes.avatarButton}
          data-testid={TEST_ID.TOP_NAV_AVATAR_MENU}
        >
          {isImpersonating && <ImpersonatingBadge id={impersonatingBadgeId} />}
          <Avatar variant="menu" name={userName} alt={userName} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {isImpersonating && (
          <Box p="xs">
            <Box className={classes.impersonatingDetails}>
              <Text fw={600}>Impersonating:</Text>
              {/* The badge shows as soon as the session says so; the name waits on the fetch. */}
              <Text>{userName}</Text>
              {impersonator && (
                <>
                  <Text fw={600}>Actual User:</Text>
                  <Text>
                    {impersonator.firstName} {impersonator.lastName}
                  </Text>
                </>
              )}
            </Box>
            <Divider mt="xs" />
          </Box>
        )}
        {menuItems?.map((item) => (
          <Menu.Item key={item.href} component={Link} href={item.href}>
            {item.label}
          </Menu.Item>
        ))}
        {onLogout && (
          <Menu.Item onClick={onLogout} data-testid={TEST_ID.TOP_NAV_LOGOUT}>
            {t('Log out')}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default TopNavAvatarMenu;
